<template>
  <div class="trash-input">
    <div class="vue-search">
      <input
        id="search-address"
        v-model="address"
        type="text"
        name="a"
        placeholder="Enter a street address"
        class="search-field"
        @change="getAddressInfo()"
      >
      <button
        v-if="address"
        class="clear-search-btn"
        @click="clearAddress()"
      >
        <i class="fas fa-times" />
      </button>
      <button
        class="search-submit"
        @click="getAddressInfo()"
      >
        <i class="fa-solid fa-magnifying-glass" />
      </button>
    </div>
    <p 
      v-if="!trashDay"
      class="supplementary-text"
    >
      Enter your address to see your regular trash and recycling collection schedule.
    </p>
    <div 
      v-if="showSearchInput"
      class="search-input"
    >
      <button 
        class="clear-label"
        @click="clearAddress()"
      >
        Clear
      </button>
      <div class="address-label">
        Address
      </div>
      <div class="address">
        {{ displayAddress }}
      </div>
    </div>
    <div
      v-if="trashDay"
      class="response"
    >
      <div class="collection-div">
        <div
          v-if="normalTrashDay"
        >
          <div 
            class="collection-day-label"
          >
            <i class="prs fa-solid fa-trash-can" />Trash collection
          </div>
          <div 
            class="collection-day"
          >
            {{ normalTrashDay }} <span v-if="secondaryTrashDay">and {{ secondaryTrashDay }}</span>
          </div>
          <hr>
          <div 
            class="collection-day-label"
          >
            <i class="prs fa-solid fa-recycle" />Recycling collection
          </div>
          <div 
            class="collection-day"
          >
            {{ normalTrashDay }}
          </div>
        </div>
        <div 
          v-if="!showHeading" 
          class="collection-day error-message"
        >
          {{ trashDay }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'TrashInput',
  props: {
    holidays: {
      type: Array,
      default: () => {
        return [];
      },
    },
    delay: {
      type: Boolean,
      required: true,
    },
    undetermined: {
      type: Boolean,
      required: true,
    },
  },
  data: function() {
    return {
      addressInfo: null,
      address: '',
      displayAddress: '',
      trashDay: null,
      normalTrashDay: null,
      secondaryTrashDay: null,
      calculatedDate: null,
      showSearchInput: false,
      showHeading: true,
    };
  },
  mounted () {

  },
  methods: {
    getAddressInfo () {
      let vm = this;
      if (vm.address != null && vm.address != '') {

        axios.get( 'https://api.phila.gov/ais/v1/addresses/'+this.address.replace(/ /g, '%20') +'?gatekeeperKey=12070257c23a728f3c09f1d0d6c7d53b' )
          .then(async (result) => {
            
            vm.addressInfo = result.data;
            vm.showSearchInput = true;
            vm.trashDay = vm.dateAndAddress(vm.addressInfo.features[0].properties.rubbish_recycle_day );
            vm.normalTrashDay = vm.abbrevToDays( vm.addressInfo.features[0].properties.rubbish_recycle_day );
            vm.secondaryTrashDay = vm.abbrevToDays( vm.addressInfo.features[0].properties.secondary_rubbish_day );
            vm.displayAddress = vm.addressInfo.features[0].properties.street_address;
            vm.showHeading = true;
            return true;
          }).catch(() => {
            vm.addressInfo = null;
            vm.normalTrashDay = null;
            vm.showSearchInput = true;
            vm.displayAddress = vm.address;
            vm.trashDay = 'No address found for that search. Please try again.';
            vm.showHeading = false;
            return false;
          });
      } else {
        vm.addressInfo = null;
        vm.showSearchInput = true;
        vm.trashDay = 'Please enter an address to search.';
        vm.normalTrashDay = null;
        vm.showHeading = false;
      }
    },
    dateAndAddress( collectionDay ) {
      // takes normal collection date, and if a holiday is occuring
      // returns the day of week and date of next collection
      let vm = this;
      let calculatedDate;
      let standardCollectionDate = moment().isoWeekday( vm.dayToNum(vm.abbrevToFullDay( collectionDay )) );
      // if normal calculation date is during a holiday period, return the calculated collection date
      calculatedDate = vm.calculateCollectionDate( standardCollectionDate, collectionDay );
      // if calculated collection date is before today
      if (calculatedDate < moment() ) {
        let nextStandardCollectionDate = standardCollectionDate.add(1, 'week'); // set standard collection date for next week
        calculatedDate = vm.calculateCollectionDate( nextStandardCollectionDate, collectionDay ); // check if holiday would impact next week
        calculatedDate.add(1, 'week'); // after checking, re-set the calculated date to be next week. calculatedCollectionDate method returns day of week for current week
      }
      // if calculated collection date is today or later this week
      return calculatedDate.format('dddd') + ', ' + calculatedDate.format('MMMM D');
    },
    // returns calculated collection date for current week given collection date, collection day of week, and holiday list
    calculateCollectionDate( collectionDate, collectionDay ) {
      // takes standard collection
      let vm = this;
      if(vm.duringHoliday( collectionDate ) == true || vm.delay) {
        return moment().isoWeekday(vm.dayToNum(vm.holidayAdjust( collectionDay )));
      }
      return moment().isoWeekday(vm.dayToNum(vm.abbrevToFullDay( collectionDay )));
    },
    duringHoliday( collectionDate ) {
      let vm = this;
      let delayed = false;
      vm.holidays.forEach( function(holiday) {
        if ( collectionDate.isBetween(holiday['start_date'], holiday['end_date']) ) {
          delayed = true;
        }
      });
      return delayed;
    },
    clearAddress() {
      let vm = this;
      vm.showSearchInput = false;
      vm.address = null;
      vm.trashDay = null;
    },
    abbrevToFullDay(abbrev) {
      switch(abbrev) {
      case 'SUN': return 'Sunday';
      case 'MON': return 'Monday';
      case 'TUE': return 'Tuesday';
      case 'WED': return 'Wednesday';
      case 'THU': return 'Thursday';
      case 'FRI': return 'Friday';
      case 'SAT': return 'Saturday';
      default: return null;
      }
    },
    abbrevToDays(abbrev) {
      switch(abbrev) {
      case 'SUN': return 'Sundays';
      case 'MON': return 'Mondays';
      case 'TUE': return 'Tuesdays';
      case 'WED': return 'Wednesdays';
      case 'THU': return 'Thursdays';
      case 'FRI': return 'Fridays';
      case 'SAT': return 'Saturdays';
      default: return null;
      }
    },
    holidayAdjust(day) {
      switch(day) {
      case 'SUN': return 'Monday';
      case 'MON': return 'Tuesday';
      case 'TUE': return 'Wednesday';
      case 'WED': return 'Thursday';
      case 'THU': return 'Friday';
      case 'FRI': return 'Saturday';
      case 'SAT': return 'Sunday';
      default: return null;
      }
    },
    dayToNum(day) {
      switch(day) {
      case 'Monday': return 1;
      case 'Tuesday': return 2;
      case 'Wednesday': return 3;
      case 'Thursday': return 4;
      case 'Friday': return 5;
      case 'Saturday': return 6;
      case 'Sunday': return 7;
      default: return null;
      }
    },
  },
};
</script>
